import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Keep the desktop overflow popover open while the pointer crosses the visual gap. */
const OVERFLOW_CLOSE_DELAY_MS = 650;

/** Extra pointer tolerance around the More trigger and its popover. */
const OVERFLOW_SAFE_MARGIN_PX = 14;

/** Matches the compact desktop overflow width defined in laus-editorial.css. */
const OVERFLOW_POPOVER_MAX_WIDTH_PX = 300;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#overflowPointerEnterListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.refs.overflowMenu?.addEventListener('pointerenter', this.#overflowHostPointerEnterListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerenter', this.#overflowPointerEnterListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.refs.overflowMenu?.removeEventListener('pointerenter', this.#overflowHostPointerEnterListener);
    this.#stopOverflowPointerGuard();
    this.#clearDeactivateTimer();
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    if (this.dataset.overflowExpanded === 'true' && this.#overflowTrigger) {
      requestAnimationFrame(() => this.#positionOverflowPopover(this.#overflowTrigger));
    }
  }, 100);

  #overflowSubmenuListener = (event) => {
    if (this.#isInsideOverflowSurface(event.relatedTarget) || this.#isInsideMoreTrigger(event.relatedTarget)) {
      this.#clearDeactivateTimer();
      return;
    }

    this.#scheduleDeactivate();
  };

  #overflowPointerEnterListener = () => {
    this.#clearDeactivateTimer();
  };

  #overflowHostPointerEnterListener = () => {
    if (this.dataset.overflowExpanded === 'true') {
      this.#clearDeactivateTimer();
    }
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;

  #clearDeactivateTimer() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  #scheduleDeactivate(delay = OVERFLOW_CLOSE_DELAY_MS) {
    this.#clearDeactivateTimer();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate();
    }, delay);
  }

  /**
   * Shadow DOM is not included in Element.closest()/contains() checks from the
   * light DOM. Check both the overflow-list host and its open shadow root so
   * moving into the popover is treated as staying inside the same menu.
   * @param {EventTarget | null} target
   */
  #isInsideOverflowSurface(target) {
    if (!(target instanceof Node)) return false;

    return Boolean(
      this.refs.overflowMenu?.contains(target) ||
        this.refs.overflowMenu?.shadowRoot?.contains(target) ||
        this.overflowMenu?.contains(target)
    );
  }

  /** @param {EventTarget | null} target */
  #isInsideMoreTrigger(target) {
    return target instanceof Element && Boolean(target.closest('[slot="more"]'));
  }

  /** @type {HTMLElement | null} */
  #overflowTrigger = null;

  #overflowPointerGuardActive = false;

  #startOverflowPointerGuard() {
    if (this.#overflowPointerGuardActive) return;

    this.#overflowPointerGuardActive = true;
    document.addEventListener('pointermove', this.#overflowPointerMoveListener, true);
  }

  #stopOverflowPointerGuard() {
    if (!this.#overflowPointerGuardActive) return;

    this.#overflowPointerGuardActive = false;
    document.removeEventListener('pointermove', this.#overflowPointerMoveListener, true);
  }

  #overflowPointerMoveListener = (event) => {
    if (this.dataset.overflowExpanded !== 'true' || !this.#overflowTrigger) return;

    if (this.#isPointInsideOverflowInteraction(event.clientX, event.clientY)) {
      this.#clearDeactivateTimer();
      return;
    }

    this.#scheduleDeactivate();
  };

  /**
   * Keep the trigger, popover, and the short corridor between them as one
   * interaction surface. Geometry is used here because the popover lives in
   * the overflow-list shadow root while its trigger is a slotted light-DOM
   * element; pointerleave alone is not reliable across that boundary.
   * @param {number} x
   * @param {number} y
   */
  #isPointInsideOverflowInteraction(x, y) {
    const trigger = this.#overflowTrigger;
    const surface = this.overflowMenu;
    if (!trigger || !surface) return false;

    const triggerRect = trigger.getBoundingClientRect();
    const surfaceRect = surface.getBoundingClientRect();
    const margin = OVERFLOW_SAFE_MARGIN_PX;

    const isInsideRect = (rect) =>
      x >= rect.left - margin &&
      x <= rect.right + margin &&
      y >= rect.top - margin &&
      y <= rect.bottom + margin;

    if (isInsideRect(triggerRect) || isInsideRect(surfaceRect)) return true;

    const corridorTop = Math.min(triggerRect.bottom, surfaceRect.top) - margin;
    const corridorBottom = Math.max(triggerRect.bottom, surfaceRect.top) + margin;
    const corridorLeft = Math.min(triggerRect.left, surfaceRect.left) - margin;
    const corridorRight = Math.max(triggerRect.right, surfaceRect.right) + margin;

    return x >= corridorLeft && x <= corridorRight && y >= corridorTop && y <= corridorBottom;
  }

  /**
   * Place the compact More popover directly below its trigger. The overflow
   * host spans the whole navigation row, so a fixed left value positions the
   * popover at the far-left edge and creates a large dead zone.
   * @param {HTMLElement} trigger
   */
  #positionOverflowPopover(trigger) {
    const host = this.refs.overflowMenu;
    if (!(host instanceof HTMLElement)) return;

    const hostRect = host.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const viewportPadding = 16;
    const popoverWidth = Math.min(OVERFLOW_POPOVER_MAX_WIDTH_PX, window.innerWidth - viewportPadding * 2);
    const viewportLeft = Math.min(
      Math.max(triggerRect.left, viewportPadding),
      window.innerWidth - popoverWidth - viewportPadding
    );
    const hostLeft = viewportLeft - hostRect.left;

    host.style.setProperty('--laus-overflow-popover-left', `${Math.round(hostLeft)}px`);
    host.style.setProperty('--laus-overflow-popover-width', `${Math.round(popoverWidth)}px`);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#clearDeactivateTimer();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const sourceItem = event.target.closest('.menu-list__list-item') || event.target;
    const sourceSlot = sourceItem.getAttribute('slot');
    const isMoreTrigger = sourceSlot === 'more';
    const isOverflowItem = sourceSlot === 'overflow';
    const item = findMenuItem(sourceItem);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return;

    if (isMoreTrigger) {
      this.#overflowTrigger = item;
      this.#positionOverflowPopover(item);
      this.#startOverflowPointerGuard();
    } else if (isOverflowItem) {
      // Keep the original More trigger as the geometry anchor while the
      // pointer moves across links inside the floating popover.
      this.#startOverflowPointerGuard();
    } else {
      this.#overflowTrigger = null;
      this.#stopOverflowPointerGuard();
    }

    const isDefaultSlot = !isMoreTrigger && !isOverflowItem;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    const headerPanelHeight = isDefaultSlot ? finalHeight : 0;

    this.headerComponent.style.setProperty('--submenu-height', `${headerPanelHeight}px`);
    this.#setFullOpenHeaderHeight(headerPanelHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowItem =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    const isMovingToOverflowSurface = this.#isInsideOverflowSurface(event.relatedTarget);
    const isMovingToMoreTrigger = this.#isInsideMoreTrigger(event.relatedTarget);

    if (
      isMovingWithinMenu ||
      isMovingToOverflowItem ||
      isMovingToOverflowSurface ||
      isMovingToMoreTrigger ||
      isMovingToSubmenu
    ) {
      this.#clearDeactivateTimer();
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    const item = this.#state.activeItem;
    const isOverflowInteraction = Boolean(item?.closest('[slot="more"]')) || this.dataset.overflowExpanded === 'true';

    if (isOverflowInteraction && event instanceof PointerEvent) {
      this.#startOverflowPointerGuard();

      if (this.#isPointInsideOverflowInteraction(event.clientX, event.clientY)) {
        this.#clearDeactivateTimer();
      } else {
        this.#scheduleDeactivate();
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#clearDeactivateTimer();

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#stopOverflowPointerGuard();
    this.#overflowTrigger = null;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  const item = element.querySelector('[ref="menuitem"]') || element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
